import { useState } from 'react';

interface ShareModalProps {
  onClose: () => void;
  fortune: string;
}

export default function ShareModal({ onClose, fortune }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`🔮 ${fortune} - Neon Fortune Cookie`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadScreenshot = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 1080;
    canvas.height = 1920;

    // Dynamic gradient background with multiple layers
    const bgGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    bgGradient.addColorStop(0, '#0a0015');
    bgGradient.addColorStop(0.3, '#1a0033');
    bgGradient.addColorStop(0.5, '#2d0052');
    bgGradient.addColorStop(0.7, '#1a0033');
    bgGradient.addColorStop(1, '#000a1a');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Radial gradient overlays for depth
    const radial1 = ctx.createRadialGradient(200, 300, 0, 200, 300, 600);
    radial1.addColorStop(0, 'rgba(236, 72, 153, 0.4)');
    radial1.addColorStop(0.5, 'rgba(168, 85, 247, 0.2)');
    radial1.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = radial1;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const radial2 = ctx.createRadialGradient(880, 1600, 0, 880, 1600, 700);
    radial2.addColorStop(0, 'rgba(6, 182, 212, 0.4)');
    radial2.addColorStop(0.5, 'rgba(168, 85, 247, 0.2)');
    radial2.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = radial2;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Diagonal light streaks
    ctx.save();
    ctx.globalAlpha = 0.15;
    const streakGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    streakGradient.addColorStop(0, 'rgba(255, 0, 255, 0)');
    streakGradient.addColorStop(0.4, 'rgba(255, 0, 255, 0.6)');
    streakGradient.addColorStop(0.5, 'rgba(0, 255, 255, 0.6)');
    streakGradient.addColorStop(0.6, 'rgba(255, 0, 255, 0.6)');
    streakGradient.addColorStop(1, 'rgba(0, 255, 255, 0)');
    ctx.fillStyle = streakGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Subtle grid pattern
    ctx.strokeStyle = 'rgba(255, 0, 255, 0.08)';
    ctx.lineWidth = 1;
    for (let i = 0; i < canvas.width; i += 60) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
    }
    for (let i = 0; i < canvas.height; i += 60) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }

    // Top section - Logo/Brand
    ctx.font = 'bold 56px "Press Start 2P", monospace';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#ff00ff';
    ctx.shadowBlur = 40;
    ctx.fillText('🔮 NEON', canvas.width / 2, 180);
    
    ctx.font = 'bold 48px "Press Start 2P", monospace';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 40;
    ctx.fillText('FORTUNE', canvas.width / 2, 260);

    // Decorative line under title
    const lineGradient = ctx.createLinearGradient(200, 320, 880, 320);
    lineGradient.addColorStop(0, 'rgba(236, 72, 153, 0)');
    lineGradient.addColorStop(0.5, 'rgba(236, 72, 153, 1)');
    lineGradient.addColorStop(1, 'rgba(6, 182, 212, 0)');
    ctx.strokeStyle = lineGradient;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(200, 320);
    ctx.lineTo(880, 320);
    ctx.stroke();

    // Main fortune container with modern card design
    const cardY = 450;
    const cardHeight = 900;
    
    // Card shadow/glow
    ctx.shadowColor = 'rgba(236, 72, 153, 0.6)';
    ctx.shadowBlur = 60;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fillRect(80, cardY, canvas.width - 160, cardHeight);

    // Card gradient background
    const cardGradient = ctx.createLinearGradient(80, cardY, 80, cardY + cardHeight);
    cardGradient.addColorStop(0, 'rgba(30, 0, 60, 0.9)');
    cardGradient.addColorStop(0.5, 'rgba(20, 0, 40, 0.95)');
    cardGradient.addColorStop(1, 'rgba(10, 0, 30, 0.9)');
    ctx.fillStyle = cardGradient;
    ctx.fillRect(80, cardY, canvas.width - 160, cardHeight);

    // Card border with gradient
    const borderGradient = ctx.createLinearGradient(80, cardY, canvas.width - 80, cardY + cardHeight);
    borderGradient.addColorStop(0, '#ec4899');
    borderGradient.addColorStop(0.5, '#a855f7');
    borderGradient.addColorStop(1, '#06b6d4');
    ctx.strokeStyle = borderGradient;
    ctx.lineWidth = 6;
    ctx.strokeRect(80, cardY, canvas.width - 160, cardHeight);

    // Corner accents
    const cornerSize = 40;
    ctx.strokeStyle = '#06b6d4';
    ctx.lineWidth = 8;
    
    // Top-left
    ctx.beginPath();
    ctx.moveTo(80 - 4, cardY + cornerSize);
    ctx.lineTo(80 - 4, cardY - 4);
    ctx.lineTo(80 + cornerSize, cardY - 4);
    ctx.stroke();

    // Top-right
    ctx.beginPath();
    ctx.moveTo(canvas.width - 80 - cornerSize, cardY - 4);
    ctx.lineTo(canvas.width - 80 + 4, cardY - 4);
    ctx.lineTo(canvas.width - 80 + 4, cardY + cornerSize);
    ctx.stroke();

    // Bottom-left
    ctx.beginPath();
    ctx.moveTo(80 - 4, cardY + cardHeight - cornerSize);
    ctx.lineTo(80 - 4, cardY + cardHeight + 4);
    ctx.lineTo(80 + cornerSize, cardY + cardHeight + 4);
    ctx.stroke();

    // Bottom-right
    ctx.beginPath();
    ctx.moveTo(canvas.width - 80 - cornerSize, cardY + cardHeight + 4);
    ctx.lineTo(canvas.width - 80 + 4, cardY + cardHeight + 4);
    ctx.lineTo(canvas.width - 80 + 4, cardY + cardHeight - cornerSize);
    ctx.stroke();

    // Fortune emoji decoration
    ctx.font = '120px Arial';
    ctx.shadowColor = '#ff00ff';
    ctx.shadowBlur = 50;
    ctx.textAlign = 'center';
    ctx.fillText('🥠', canvas.width / 2, cardY + 150);

    // Opening quote
    ctx.font = 'bold 100px serif';
    ctx.fillStyle = '#ec4899';
    ctx.shadowColor = '#ec4899';
    ctx.shadowBlur = 30;
    ctx.textAlign = 'left';
    ctx.fillText('"', 140, cardY + 280);

    // Fortune text with better wrapping
    ctx.font = 'bold 42px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';
    ctx.shadowBlur = 20;
    ctx.textAlign = 'center';
    
    const words = fortune.split(' ');
    let line = '';
    let y = cardY + 380;
    const maxWidth = 800;
    const lineHeight = 65;
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && i > 0) {
        lines.push(line.trim());
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());

    // Center the text block vertically
    const totalHeight = lines.length * lineHeight;
    y = cardY + (cardHeight - totalHeight) / 2 + 50;

    lines.forEach((textLine) => {
      ctx.fillText(textLine, canvas.width / 2, y);
      y += lineHeight;
    });

    // Closing quote
    ctx.font = 'bold 100px serif';
    ctx.fillStyle = '#06b6d4';
    ctx.shadowColor = '#06b6d4';
    ctx.shadowBlur = 30;
    ctx.textAlign = 'right';
    ctx.fillText('"', canvas.width - 140, cardY + cardHeight - 100);

    // Decorative dots
    const dotY = cardY + cardHeight + 100;
    const dots = [
      { x: canvas.width / 2 - 60, color: '#ec4899' },
      { x: canvas.width / 2, color: '#a855f7' },
      { x: canvas.width / 2 + 60, color: '#06b6d4' }
    ];

    dots.forEach(dot => {
      ctx.fillStyle = dot.color;
      ctx.shadowColor = dot.color;
      ctx.shadowBlur = 25;
      ctx.beginPath();
      ctx.arc(dot.x, dotY, 12, 0, Math.PI * 2);
      ctx.fill();
    });

    // Bottom branding with style
    ctx.shadowBlur = 0;
    ctx.font = 'bold 32px Arial';
    ctx.fillStyle = '#888';
    ctx.textAlign = 'center';
    ctx.fillText('happyplaceonline.com', canvas.width / 2, 1780);

    // Subtle scanline effect
    ctx.globalAlpha = 0.03;
    for (let i = 0; i < canvas.height; i += 4) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, i, canvas.width, 2);
    }
    ctx.globalAlpha = 1;

    // Download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `neon-fortune-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-black border-2 sm:border-4 border-purple-400 rounded-lg p-6 sm:p-8 max-w-md sm:max-w-lg w-full neon-modal-glow animate-scaleIn">
        <h2 className="text-purple-400 font-bold text-lg sm:text-xl md:text-2xl mb-4 sm:mb-6 text-center neon-text-modal" style={{ fontFamily: '"Press Start 2P", monospace' }}>
          <span className="text-xs sm:text-sm">SHARE YOUR FORTUNE</span>
        </h2>

        <div className="bg-black/60 border-2 border-cyan-400/40 rounded p-3 sm:p-4 mb-4 sm:mb-6">
          <p className="text-white text-sm sm:text-base text-center leading-relaxed">
            {fortune}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <button
            onClick={copyToClipboard}
            className="px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-sm uppercase tracking-wider border-2 border-pink-400 hover:scale-105 transition-all duration-300 neon-button whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-copy-line mr-2"></i>
            <span className="text-[10px] sm:text-xs">{copied ? 'Copied!' : 'Copy'}</span>
          </button>

          <button
            onClick={downloadScreenshot}
            className="px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm uppercase tracking-wider border-2 border-purple-400 hover:scale-105 transition-all duration-300 neon-button whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line mr-2"></i>
            <span className="text-[10px] sm:text-xs">Screenshot</span>
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold text-sm sm:text-base uppercase tracking-wider border-2 border-gray-500 hover:scale-105 transition-all duration-300 neon-button whitespace-nowrap cursor-pointer"
          style={{ fontFamily: '"Press Start 2P", monospace' }}
        >
          <span className="text-[10px] sm:text-xs">Close</span>
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }

        .neon-modal-glow {
          box-shadow: 
            0 0 30px rgba(168, 85, 247, 0.5),
            0 0 60px rgba(168, 85, 247, 0.3),
            inset 0 0 20px rgba(168, 85, 247, 0.1);
        }

        .neon-text-modal {
          text-shadow: 
            0 0 10px #a855f7,
            0 0 20px #a855f7;
        }

        .neon-button {
          box-shadow: 
            0 0 20px currentColor,
            0 0 40px currentColor,
            inset 0 0 10px rgba(255, 255, 255, 0.2);
        }

        .neon-button:hover {
          box-shadow: 
            0 0 30px currentColor,
            0 0 60px currentColor,
            inset 0 0 20px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
}
